import React, { useEffect, useRef, useState } from 'react'
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import { XIcon } from '../icons'

// Simple Flash Icon
const FlashIcon = ({ className, on }: { className?: string; on: boolean }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill={on ? "currentColor" : "none"} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
)

const SUPPORTED_FORMATS = [
    Html5QrcodeSupportedFormats.QR_CODE,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.CODE_93,
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
    Html5QrcodeSupportedFormats.ITF,
    Html5QrcodeSupportedFormats.CODABAR,
]

type CameraScannerProps = {
    isOpen: boolean
    onClose: () => void
    onScan: (barcode: string) => void
    title?: string
    hint?: string
    permissionDeniedMessage?: string
    notSupportedMessage?: string
    closeButtonLabel?: string
}

export const CameraScanner = ({
    isOpen,
    onClose,
    onScan,
    title = 'Scan Badge with Camera',
    hint = 'Position the badge barcode in the camera view',
    permissionDeniedMessage = 'Camera access was denied. Please allow camera access to use this feature.',
    notSupportedMessage = 'Camera scanning is not supported on this device.',
    closeButtonLabel = 'Close'
}: CameraScannerProps) => {
    const [error, setError] = useState<string | null>(null)
    const [isInitializing, setIsInitializing] = useState(false)
    const [torchOn, setTorchOn] = useState(false)
    const [showTorchButton, setShowTorchButton] = useState(false)

    const scannerRef = useRef<Html5Qrcode | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const hasScannedRef = useRef(false)

    useEffect(() => {
        if (!isOpen) {
            // Cleanup logic
            if (scannerRef.current) {
                const state = scannerRef.current.getState()
                if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                    scannerRef.current.stop().catch(() => { })
                }
                scannerRef.current = null
            }
            setError(null)
            hasScannedRef.current = false
            setTorchOn(false)
            setShowTorchButton(false)
            return
        }

        const initScanner = async () => {
            if (!containerRef.current || scannerRef.current) return

            setIsInitializing(true)
            setError(null)

            try {
                const scanner = new Html5Qrcode('camera-scanner-container', {
                    formatsToSupport: SUPPORTED_FORMATS,
                    verbose: false
                })
                scannerRef.current = scanner

                await scanner.start(
                    { facingMode: 'environment' },
                    {
                        fps: 15,
                        qrbox: (viewfinderWidth, viewfinderHeight) => {
                            const size = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.9)
                            return { width: size, height: size }
                        },
                        aspectRatio: 1.0
                    },
                    (decodedText) => {
                        if (hasScannedRef.current) return
                        hasScannedRef.current = true

                        if (scannerRef.current) {
                            scannerRef.current.stop().then(() => {
                                onScan(decodedText)
                                onClose()
                            }).catch(() => {
                                onScan(decodedText)
                                onClose()
                            })
                        }
                    },
                    () => { }
                )

                // Check for Torch Capability after starting
                try {
                    // This creates a camera stream reference just to check capabilities
                    // Note: Html5Qrcode keeps track of the running track internally, 
                    // but getting it directly via the library API is cleaner if supported.
                    // Since the library abstracts the track, we use its built-in method applyVideoConstraints to test or check features.

                    // However, standard HTML5Qrcode doesn't expose getCapabilities directly easily.
                    // We can attempt to toggle it to see if it throws or check track constraints if we had the track.
                    // A safe way is to assume it might work if it's a mobile environment, or just try to get the running track.

                    // Access the internal video element to get the track capabilities
                    const videoElement = document.querySelector('#camera-scanner-container video') as HTMLVideoElement;
                    if (videoElement && videoElement.srcObject) {
                        const stream = videoElement.srcObject as MediaStream;
                        const track = stream.getVideoTracks()[0];
                        const capabilities = track.getCapabilities();
                        // @ts-ignore - 'torch' is standard but TS might complain depending on lib version
                        if (capabilities.torch) {
                            setShowTorchButton(true);
                        }
                    }
                } catch (e) {
                    console.warn("Could not detect torch capability", e);
                }

            } catch (err) {
                // ... (Existing Error Handling)
                if (err instanceof Error) {
                    if (err.message.includes('Permission') || err.message.includes('NotAllowed')) {
                        setError(permissionDeniedMessage)
                    } else if (err.message.includes('NotFound') || err.message.includes('NotSupported')) {
                        setError(notSupportedMessage)
                    } else {
                        setError(err.message)
                    }
                } else {
                    setError(notSupportedMessage)
                }
            } finally {
                setIsInitializing(false)
            }
        }

        const timeoutId = setTimeout(initScanner, 100)
        return () => clearTimeout(timeoutId)
    }, [isOpen, onScan, onClose, permissionDeniedMessage, notSupportedMessage])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (scannerRef.current) {
                const state = scannerRef.current.getState()
                if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                    scannerRef.current.stop().catch(() => { })
                }
            }
        }
    }, [])

    const toggleTorch = async () => {
        if (scannerRef.current) {
            try {
                const state = scannerRef.current.getState();
                if (state === Html5QrcodeScannerState.SCANNING) {
                    await scannerRef.current.applyVideoConstraints({
                        advanced: [{ torch: !torchOn }] as any // Type assertion for constraints
                    });
                    setTorchOn(!torchOn);
                }
            } catch (err) {
                console.error("Failed to toggle torch", err);
                // If toggling fails, hide the button
                setShowTorchButton(false);
            }
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        aria-label={closeButtonLabel}
                    >
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

\                <div className="p-4">
                    {error ? (
                        <div className="text-center py-8">
                            <div className="text-danger-600 dark:text-danger-400 mb-4">
                                {error}
                            </div>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                            >
                                {closeButtonLabel}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ minHeight: '300px' }}>
                                <div
                                    id="camera-scanner-container"
                                    ref={containerRef}
                                    className="w-full h-full"
                                />

                                {showTorchButton && !isInitializing && (
                                    <button
                                        onClick={toggleTorch}
                                        className={`absolute top-4 right-4 p-2 rounded-full backdrop-blur-sm transition-colors ${torchOn
                                                ? 'bg-yellow-400/80 text-black hover:bg-yellow-400'
                                                : 'bg-black/40 text-white hover:bg-black/60'
                                            }`}
                                        title="Toggle Flash"
                                    >
                                        <FlashIcon className="w-6 h-6" on={torchOn} />
                                    </button>
                                )}
                            </div>

                            {isInitializing && (
                                <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                </div>
                            )}
                            <p className="mt-3 text-sm text-center text-gray-500 dark:text-gray-400">
                                {hint}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}